/**
 * Tenant Resolution for Multi-tenant POS
 * 
 * Extracts tenant slug from subdomain and resolves Supabase credentials
 * from the admin registry.
 */

export interface TenantInfo {
  slug: string;
  name: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  planName: string;
  licensedLocations: number;
  licensedRegisters: number;
  subscriptionStatus: string;
}

export interface TenantResolutionResult {
  tenant: TenantInfo | null;
  error: string | null;
  isLoading: boolean;
}

const ADMIN_API_URL = import.meta.env.VITE_ADMIN_API_URL || 'https://phoenix.blazepos.app';
const POS_BASE_DOMAIN = import.meta.env.VITE_POS_BASE_DOMAIN || 'blazepos.app';

// Cache tenant info in session storage
const TENANT_CACHE_KEY = 'blazepos_tenant_info';
const TENANT_CACHE_EXPIRY_KEY = 'blazepos_tenant_expiry';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Extract tenant slug from the current hostname
 * e.g., "joes.blazepos.app" -> "joes"
 */
export function extractTenantSlug(): string | null {
  const hostname = window.location.hostname;
  
  // Development mode - check for query param or localStorage
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');
    if (tenantParam) {
      localStorage.setItem('dev_tenant_slug', tenantParam);
      return tenantParam;
    }
    return localStorage.getItem('dev_tenant_slug');
  }
  
  // Production - extract from subdomain
  // Expected format: {tenant}.blazepos.app
  const parts = hostname.split('.');
  
  // Must have at least 3 parts for subdomain.blazepos.app
  if (parts.length >= 3) {
    // First part is the tenant slug
    return parts[0];
  }
  
  // Could also be custom domain - in that case, we need to look it up
  // For now, return null and let the app handle it
  return null;
}

/**
 * Get cached tenant info if still valid
 */
function getCachedTenant(): TenantInfo | null {
  try {
    const expiry = sessionStorage.getItem(TENANT_CACHE_EXPIRY_KEY);
    if (expiry && Date.now() < parseInt(expiry, 10)) {
      const cached = sessionStorage.getItem(TENANT_CACHE_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

/**
 * Cache tenant info
 */
function cacheTenant(tenant: TenantInfo): void {
  try {
    sessionStorage.setItem(TENANT_CACHE_KEY, JSON.stringify(tenant));
    sessionStorage.setItem(TENANT_CACHE_EXPIRY_KEY, String(Date.now() + CACHE_DURATION_MS));
  } catch {
    // Ignore cache errors
  }
}

/**
 * Clear tenant cache
 */
export function clearTenantCache(): void {
  try {
    sessionStorage.removeItem(TENANT_CACHE_KEY);
    sessionStorage.removeItem(TENANT_CACHE_EXPIRY_KEY);
  } catch {
    // Ignore cache errors
  }
}

/**
 * Resolve tenant from admin API
 * Returns Supabase credentials for the tenant's project
 */
export async function resolveTenant(slug: string): Promise<TenantInfo> {
  // Check cache first
  const cached = getCachedTenant();
  if (cached && cached.slug === slug) {
    return cached;
  }
  
  // In development without admin API, use env vars directly
  if (import.meta.env.DEV && !import.meta.env.VITE_ADMIN_API_URL) {
    const devTenant: TenantInfo = {
      slug: slug || 'dev',
      name: 'Development',
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL || 'http://localhost:54321',
      supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
      planName: 'Pro',
      licensedLocations: 10,
      licensedRegisters: 20,
      subscriptionStatus: 'active',
    };
    cacheTenant(devTenant);
    return devTenant;
  }
  
  // Call admin API to resolve tenant
  const response = await fetch(`${ADMIN_API_URL}/api/client/resolve?slug=${encodeURIComponent(slug)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Organization not found');
    }
    throw new Error('Failed to resolve tenant');
  }
  
  const data = await response.json();
  
  const tenant: TenantInfo = {
    slug: data.slug,
    name: data.name,
    supabaseUrl: data.supabase_url,
    supabaseAnonKey: data.supabase_anon_key,
    planName: data.plan_name,
    licensedLocations: data.licensed_locations,
    licensedRegisters: data.licensed_registers,
    subscriptionStatus: data.subscription_status,
  };
  
  cacheTenant(tenant);
  return tenant;
}

/**
 * Check if current host is the admin domain
 */
export function isAdminDomain(): boolean {
  const hostname = window.location.hostname;
  return hostname === 'phoenix.blazepos.app' || 
         hostname === 'admin.blazepos.app' || 
         hostname.startsWith('phoenix.') ||
         hostname.startsWith('admin.') ||
         hostname === 'localhost' && window.location.port === '3001';
}

/**
 * Get the full POS URL for a tenant
 */
export function getTenantPOSUrl(slug: string): string {
  if (import.meta.env.DEV) {
    return `http://localhost:3000?tenant=${slug}`;
  }
  return `https://${slug}.${POS_BASE_DOMAIN}`;
}


